import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, User, Key } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api, { formatApiError, formatDate } from "../lib/api";
import { useAuth } from "../lib/auth";

const ROLES = ["superadmin", "admin", "hr", "finance"];  // karyawan dibuat otomatis via menu Karyawan

const roleLabel = (r) => ({ superadmin: "Super Admin", admin: "Admin Lembaga", hr: "HR", finance: "Finance", karyawan: "Karyawan" }[r] || r);

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [lembagas, setLembagas] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = async () => {
    const [u, l] = await Promise.all([api.get("/users"), api.get("/lembaga").catch(() => ({ data: [] }))]);
    setItems(u.data.filter((x) => x.role !== "karyawan"));
    setLembagas(l.data);
  };
  useEffect(() => { load(); }, []);

  const lembagaMap = Object.fromEntries(lembagas.map((l) => [l.id, l.name]));

  const openNew = () => { setEditing(null); setForm({ email: "", password: "", name: "", role: "admin", lembaga_id: "" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...u, password: "", lembaga_id: u.lembaga_id || "" }); setOpen(true); };
  const save = async () => {
    if (!form.name?.trim()) { toast.error("Nama wajib diisi"); return; }
    if (!editing && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email || "")) { toast.error("Format email tidak valid"); return; }
    if (!editing && (form.password || "").length < 6) { toast.error("Password minimal 6 karakter"); return; }
    if (form.role === "admin" && !form.lembaga_id && currentUser?.role === "superadmin") { toast.error("Pilih lembaga untuk Admin Lembaga"); return; }
    try {
      const payload = { name: form.name, role: form.role, phone: form.phone, lembaga_id: form.role === "admin" ? (form.lembaga_id || null) : null };
      if (editing) {
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
      } else {
        await api.post("/users", { ...payload, email: form.email, password: form.password });
      }
      toast.success("Pengguna tersimpan"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!confirm("Hapus pengguna?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Terhapus"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resetPassword = async (u) => {
    if (!confirm(`Reset password untuk ${u.name}?`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      alert(`Password ${u.name} direset menjadi: ${data.new_password}\n\nSampaikan password ini kepada user.`);
      toast.success("Password direset");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Sistem</div>
          <h1 className="font-display text-3xl font-black text-gray-900">Manajemen Pengguna</h1>
          <p className="text-gray-600 text-sm mt-1">Akun sistem: Superadmin, Admin, HR, Finance. Untuk karyawan/guru, gunakan menu <b>Karyawan</b> — akun login otomatis dibuat.</p>
        </div>
        <Button onClick={openNew} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="user-add-button"><Plus size={16} className="mr-1.5" />Tambah Pengguna</Button>
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr><th className="px-4 py-3 font-semibold">Nama</th><th className="px-4 py-3 font-semibold">Email</th><th className="px-4 py-3 font-semibold">Role</th><th className="px-4 py-3 font-semibold">Lembaga</th><th className="px-4 py-3 font-semibold">Dibuat</th><th className="px-4 py-3 font-semibold text-right">Aksi</th></tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-[#0B8D95]/10 text-[#0B8D95] flex items-center justify-center font-bold text-xs">{u.name?.[0]}</div>
                      <div className="font-semibold">{u.name}</div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-2.5"><span className="inline-block px-2 py-0.5 rounded bg-[#CCFBF1] text-[#0B8D95] text-xs font-semibold">{roleLabel(u.role)}</span></td>
                  <td className="px-4 py-2.5 text-xs">{u.lembaga_id ? (lembagaMap[u.lembaga_id] || "-") : <span className="text-gray-400">Semua Lembaga</span>}</td>
                  <td className="px-4 py-2.5 text-xs">{formatDate(u.created_at)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={()=>resetPassword(u)} className="p-1.5 hover:bg-amber-50 rounded text-amber-600" title="Reset Password" data-testid={`user-reset-${u.id}`}><Key size={14} /></button>
                    <button onClick={()=>openEdit(u)} className="p-1.5 hover:bg-gray-100 rounded" data-testid={`user-edit-${u.id}`}><PencilSimple size={14} /></button>
                    {u.id !== currentUser?.id && <button onClick={()=>remove(u.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`user-delete-${u.id}`}><Trash size={14} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="user-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Pengguna" : "Tambah Pengguna"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nama *</Label><Input value={form.name || ""} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="user-name-input" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.email || ""} onChange={(e)=>setForm({...form, email: e.target.value})} disabled={!!editing} data-testid="user-email-input" /></div>
            <div><Label>Password {editing && "(kosongkan jika tidak diubah)"}</Label><Input type="password" value={form.password || ""} onChange={(e)=>setForm({...form, password: e.target.value})} data-testid="user-password-input" /></div>
            <div><Label>Role *</Label>
              <Select value={form.role || "admin"} onValueChange={(v)=>setForm({...form, role: v})}>
                <SelectTrigger className="bg-white" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{(currentUser?.role === "superadmin" ? ROLES : ["admin"]).map((r)=><SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.role === "admin" && (
              <div>
                <Label>Lembaga <span className="text-rose-500">*</span> <span className="text-xs text-gray-500 font-normal">(Admin hanya akses lembaga ini)</span></Label>
                <Select value={form.lembaga_id || ""} onValueChange={(v)=>setForm({...form, lembaga_id: v})}>
                  <SelectTrigger className="bg-white" data-testid="user-lembaga-select"><SelectValue placeholder="Pilih lembaga…" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">{lembagas.map((l)=><SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
            <Button onClick={save} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="user-save-button">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
